import { Injectable } from '@angular/core';
import { Business, BusinessAddress } from '@models/businesses';
import { AllAdjustmentsCalculated } from '@state/matter/adjustments/adjustments.selectors';
import { cloneDeep } from 'lodash';
import { BaseFormMapper } from '../../base-form-mapper';
import {
	Matter,
	PersonMatterClient,
	BusinessMatterClient,
	PersonMatterClientWithAssociations,
	BusinessMatterClientWithAssociations,
	CalculatedLandTransferTax,
	ClientsCapacity,
	MatterTitle,
} from '@models/matter';
import { format } from 'date-fns';
import { addressString } from '@core/util/address';
import { clientLegalName, isGuarantor } from '@core/util/client';
import { MatterMortgage } from '@models/mortgage';
import { isSet } from '@core/util/value';
import { GetDateObject } from '@core/util/datetime';
import { getPrimaryParcel } from '@core/util/parcels';
import { formatCentsToDollars } from '@core/util/money/centsToDollars';
import { MortgagePriority } from '@models/encumbrance';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { getMortgageFinancialInstitutionLender } from '@core/util/mortgage';
import { joinNames } from '@core/util/string';

@Injectable({
	providedIn: 'root',
})
export class TD_REQUEST_FOR_FUNDS_Mapper extends BaseFormMapper<TD_REQUEST_FOR_FUNDS> {
	CloserToFormMappings = Mappings;

	mergeFormData(
		formData: TD_REQUEST_FOR_FUNDS,
		matter: Matter,
		nameFormattingSettings: NameFormattingSettings,
		sellers: (PersonMatterClient | BusinessMatterClient)[],
		buyers: (PersonMatterClient | BusinessMatterClient)[],
		adjustments: AllAdjustmentsCalculated,
		business: Business,
		matterTitles: MatterTitle[],
		matterClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		lttCalculated: CalculatedLandTransferTax,
		businessAddress: BusinessAddress,
		otherParties: PersonMatterClient[],
		clientsCapacity: ClientsCapacity,
		mortgage: MatterMortgage,
	): TD_REQUEST_FOR_FUNDS {
		const _formData = formData
			? cloneDeep(formData)
			: ({} as TD_REQUEST_FOR_FUNDS);

		const financialInstitution =
			getMortgageFinancialInstitutionLender(mortgage);

		if (financialInstitution?.fax && !isSet(_formData?.faxNumber)) {
			_formData.faxNumber = financialInstitution?.fax;
		}

		if (matter?.lawyerAssigned?.name && !isSet(_formData?.lawyerName)) {
			_formData.lawyerName = matter.lawyerAssigned.name;
		}

		if (businessAddress?.phoneNumber && !isSet(_formData?.solicitorTelephono)) {
			_formData.solicitorTelephono = businessAddress.phoneNumber;
		}

		if (businessAddress?.faxNumber && !isSet(_formData?.solicitorFaxNumber)) {
			_formData.solicitorFaxNumber = businessAddress.faxNumber;
		}

		if (business?.name && !isSet(_formData?.lawfirmName)) {
			_formData.lawfirmName = business.name;
		}

		if (businessAddress && !isSet(_formData?.addressOfTheOffice)) {
			_formData.addressOfTheOffice = addressString(businessAddress);
		}

		if (matterClients?.length && !isSet(_formData?.reMortgagors)) {
			const FilteredMatterClients = matterClients.filter(
				(client) => !isGuarantor(client),
			);
			_formData.reMortgagors = joinNames(
				FilteredMatterClients.map((client) =>
					clientLegalName(client, nameFormattingSettings, {
						applyFormatting: false,
					}),
				),
			);
		}

		if (mortgage?.guarantors && !isSet(_formData?.reGuarantors)) {
			const FilteredMatterClients = matterClients?.filter(
				(client) =>
					isGuarantor(client) && mortgage.guarantors.includes(client.id),
			);
			_formData.reGuarantors = joinNames(
				FilteredMatterClients.map((client) =>
					clientLegalName(client, nameFormattingSettings, {
						applyFormatting: false,
					}),
				),
			);
		}

		if (
			mortgage?.referenceNumber &&
			!isSet(_formData?.reMortgageReferenceNumber)
		) {
			_formData.reMortgageReferenceNumber = mortgage.referenceNumber;
		}

		if (
			financialInstitution?.branch?.transitNumber &&
			!isSet(_formData?.reBranch)
		) {
			_formData.reBranch = financialInstitution.branch.transitNumber;
		}

		if (
			matter?.propertyDetails?.address &&
			!isSet(_formData?.reAddressOfProperty)
		) {
			_formData.reAddressOfProperty = addressString(
				matter.propertyDetails.address,
			);
		}

		const PrimaryTitle = getPrimaryParcel(matterTitles);
		if (PrimaryTitle?.description && !isSet(_formData?.reLegalDescription)) {
			_formData.reLegalDescription = PrimaryTitle.description;
		}

		if (mortgage?.amount && !isSet(_formData?.reMortgageAmount)) {
			_formData.reMortgageAmount = formatCentsToDollars(mortgage.amount, false);
		}

		if (matter?.closingDate && !isSet(_formData?.reMortgageClosingDate)) {
			_formData.reMortgageClosingDate = format(
				GetDateObject(matter.closingDate),
				'MMMM dd, yyyy',
			);
		}

		if (mortgage?.amount && !isSet(_formData?.theAmountOf)) {
			_formData.theAmountOf = formatCentsToDollars(mortgage.amount, false);
		}

		if (matter?.closingDate && !isSet(_formData?.forTheMortgageClosingDateOf)) {
			_formData.forTheMortgageClosingDateOf = format(
				GetDateObject(matter.closingDate),
				'MMMM dd, yyyy',
			);
		}

		if (
			mortgage?.priority &&
			!isSet(_formData?.first) &&
			!isSet(_formData?.second) &&
			!isSet(_formData?.other)
		) {
			_formData.second = 'Off';
			_formData.first = 'Off';
			_formData.other = 'Off';
			_formData.mortgagePriorityOther = '';

			if (mortgage.priority === 1) {
				_formData.first = 'On';
			} else if (mortgage.priority === 2) {
				_formData.second = 'On';
			} else {
				_formData.other = 'On';
				_formData.mortgagePriorityOther = MortgagePriority.find(
					(option) => mortgage.priority === option.value,
				)?.label;
			}
		}

		if (mortgage?.registrationNumber && !isSet(_formData?.registrationNumber)) {
			_formData.registrationNumber = mortgage.registrationNumber;
		}

		if (mortgage?.registrationDate && !isSet(_formData?.dateOfRegistration)) {
			_formData.dateOfRegistration = mortgage.registrationDate;
		}

		if (PrimaryTitle?.pin && !isSet(_formData?.primaryPin)) {
			_formData.primaryPin = PrimaryTitle.pin;
		}

		if (
			matter?.propertyDetails?.rollNumber &&
			!isSet(_formData?.taxRollNumber)
		) {
			_formData.taxRollNumber = matter.propertyDetails.rollNumber;
		}

		if (!isSet(_formData?.todaysDate)) {
			_formData.todaysDate = format(new Date(), 'MMMM dd, yyyy');
		}
		if (
			matter?.lawyerAssigned?.name &&
			!isSet(_formData?.barristerSolicitorName)
		) {
			_formData.barristerSolicitorName = matter.lawyerAssigned.name;
		}

		return _formData;
	}

	MapValues(): Partial<TD_REQUEST_FOR_FUNDS> {
		return {};
	}
}

const Mappings: TD_REQUEST_FOR_FUNDS = {
	faxNumber: 'fax number',
	attention: 'attention',
	solicitorTelephono: 'solicitor telephono',
	lawyerName: 'lawyer name',
	solicitorFaxNumber: 'solicitor fax number',
	lawfirmName: 'lawfirm name',
	addressOfTheOffice: 'address of the office',
	reMortgagors: 're mortgagors',
	reGuarantors: 're guarantors',
	reBranch: 're branch',
	reMortgageReferenceNumber: 're mortgage reference number',
	reAddressOfProperty: 're address of property',
	reLegalDescription: 're legal description',
	reMortgageAmount: 're mortgage amount',
	reMortgageClosingDate: 're mortgage closing date',

	weHaveComplied: 'we have complied',
	forTheMortgageClosingDateOf: 'for the Mortgage Closing Date of',
	theAmountOf: 'the amount of',
	dateAmountChanged: 'date amount changed',

	branch: 'Branch',
	accountNumber: 'account number',
	depositTo: 'Deposit to',
	trustAccountNumber: 'trust account number',
	ifPreviouslyApproved: 'If previously approved',
	branch2: 'Branch 2',
	institution: 'Institution',
	pickupBranchName: 'pickup branch name',
	pickupCheque: 'Pickup cheque',
	mailCourierCheque: 'Mail Courier cheque',
	theMortgagorsHaveRequested: 'The Mortgagors have requested',

	mortgagePriorityOther: 'mortgage priority other',
	other: 'Other',
	second: 'Second',
	first: 'First',

	registrationNumber: 'registration number',
	dateOfRegistration: 'date of registration',
	dateIlaSigned: 'date ila signed',

	primaryPin: 'primary pin',
	taxRollNumber: 'tax roll number',
	newMortgageClosingDateText: 'new mortgage closing date text',
	newMortgageClosingDate: 'New Mortgage Closing Date',
	newMortgageClosingAmount: 'new mortgage closing amount',
	otherNewAmount: 'Other new amount',
	returningTheMortgageProceeds: 'returning the Mortgage proceeds',
	todaysDate: 'todays date',
	barristerSolicitorName: 'Barrister Solicitor name',
};

export interface TD_REQUEST_FOR_FUNDS {
	faxNumber: string;
	attention: string;
	solicitorTelephono: string;
	lawyerName: string;
	solicitorFaxNumber: string;
	lawfirmName: string;
	addressOfTheOffice: string;
	reMortgagors: string;
	reGuarantors: string;
	reBranch: string;
	reMortgageReferenceNumber: string;
	reAddressOfProperty: string;
	reLegalDescription: string;
	reMortgageAmount: string;
	reMortgageClosingDate: string;

	weHaveComplied: string;
	forTheMortgageClosingDateOf: string;
	theAmountOf: string;
	dateAmountChanged: string;
	branch: string;
	accountNumber: string;
	depositTo: string;
	trustAccountNumber: string;
	ifPreviouslyApproved: string;
	branch2: string;
	institution: string;
	pickupBranchName: string;
	pickupCheque: string;
	mailCourierCheque: string;
	theMortgagorsHaveRequested: string;
	mortgagePriorityOther: string;
	other: string;
	second: string;
	first: string;
	registrationNumber: string;
	dateOfRegistration: string;

	dateIlaSigned: string;
	primaryPin: string;
	taxRollNumber: string;
	newMortgageClosingDateText: string;
	newMortgageClosingDate: string;
	newMortgageClosingAmount: string;
	otherNewAmount: string;
	returningTheMortgageProceeds: string;
	todaysDate: string;
	barristerSolicitorName: string;
}
